import { useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Hoisted constants — referenced by props below. Defining these inline
// inside the component caused recharts to re-create internal objects on
// every parent render (iter14 perf audit fix).
const CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const Y_DOMAIN = ["auto", "auto"];
const TOOLTIP_STYLE = {
  background: "#050505",
  border: "1px solid rgba(255,255,255,0.2)",
  fontFamily: "JetBrains Mono",
  fontSize: 12,
};
const _labelFormatter = (v) => new Date(v * 1000).toLocaleTimeString();
const _valueFormatter = (v) => Number(v).toFixed(4);

function MiniChart({ data, dataKey, color }) {
  if (!data || data.length === 0) {
    return <div className="text-xs text-[var(--mq-text-3)] p-4">No data</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={CHART_MARGIN}>
        <XAxis dataKey="ts" hide />
        <YAxis hide domain={Y_DOMAIN} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={_labelFormatter}
          formatter={_valueFormatter}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function Charts() {
  const { t } = useI18n();
  const [pnl, setPnl] = useState([]);
  const [spread, setSpread] = useState([]);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/history/pnl"),
        api.get("/history/spread"),
      ]);
      setPnl(p.data);
      setSpread(s.data);
    } catch (err) {
      console.warn("Charts /history fetch failed:", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="mq-panel" data-testid="charts-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">
          {t("charts")}
        </span>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="mq-label mb-2">{t("pnlOverTime")}</div>
          <MiniChart data={pnl} dataKey="pnl" color="#00f58c" />
        </div>
        <div>
          <div className="mq-label mb-2">{t("spreadHistory")}</div>
          <MiniChart data={spread} dataKey="spread_pct" color="#00e5ff" />
        </div>
      </div>
    </div>
  );
}
